<template>
  <timeline-browser
    ref="timelineBrowserRef"
    :period-type="periodType"
    @update:period-type="handlePeriodTypeChange"
    v-model:sort-order="sortOrder"
    :timeline="timeline"
    :pending="isPending"
    :error="timelineError"
    :preferences-ready="preferencesReady"
    :decades="displayedDecades"
    :years="displayedYears"
    :selected-decade-id="selectedDecadeId"
    :get-years-for-decade="getYearsForDecade"
    @retry="refreshTimeline"
    @select="handlePeriodSelect"
    @exit-decade="navigation.exitDecade"
    :scroll-element="scrollElement"
    :external-scroll="externalScroll"
  />
</template>

<script setup>
  const props = defineProps({
    userId: {
      type: [String, Number],
      required: true
    },
    timelineController: Object,
    timelineNavigation: Object,
    active: {
      type: Boolean,
      default: true
    },
    scrollElement: Object,
    externalScroll: Boolean
  });

  const resolvedUserId = computed(() => String(props.userId));
  const timelineBrowserRef = ref(null);
  const navigation = props.timelineNavigation || useTimelineNavigation(resolvedUserId, 'profile');
  const {navigationState, selectedDecadeId} = navigation;

  const controller = props.timelineController || useTimeline(resolvedUserId, {forcedInitialPeriodType: navigation.initialPeriodType});
  const {
    timeline,
    pending: timelinePending,
    error: timelineError,
    refresh: refreshTimeline,
    preferencesReady,
    periodType,
    setPeriodType,
    sortOrder,
    displayedDecades,
    displayedYears,
    getYearsForDecade
  } = controller;

  const isPending = computed(() => (
    !preferencesReady.value
    || timelinePending.value
  ));

  watch(navigation.initialPeriodType, type => {
    if (type) periodType.value = type;
  }, {immediate: true});
  watch([isPending, selectedDecadeId, displayedDecades, () => props.active], async ([pending, decade, decades, active]) => {
    if (active && !pending && decade && !decades.some(period => period.id === decade)) {
      await navigation.replacePeriodView('decade');
    }
  });
  useTimelineNavigationRestoration({pending: isPending, browser: timelineBrowserRef, timeline, navigationState, active: computed(() => props.active)});

  async function handlePeriodTypeChange(type) {
    setPeriodType(type);
    await navigation.replacePeriodView(type);
  }

  async function handlePeriodSelect(period) {
    if (period.type === 'decade') {
      await navigation.enterDecade(period.id);
      return;
    }

    navigationState.value = timelineBrowserRef.value?.captureNavigationState() || null;
    await navigateTo(useBuildRoute().toTimelinePeriod({
      userId: resolvedUserId.value,
      periodId: period.id,
      query: navigation.yearQuery(navigationState.value || {periodType: periodType.value})
    }));
  }

</script>
